import {
	FunctionComponent,
	ReactElement,
	useCallback
} from "react";
import {
	AddressBookContactSource,
	Overview
} from "./Overview/Overview";
import { ensureNotNil } from "client/Misc/utils";
import { SourceSelectorValue } from "./SourceSelector";
import {
	NavigationCallback,
	PredefinedPropOptionKind,
	QueryParametersFromProps,
	RouteParametersFromProps,
	connectToRouter
} from "client/Navigation";
import { useContactSourceValue } from "./Overview/useContactSourceValue";
import {
	addressBookRouting,
	convertToSourcePath,
	encodeIdForRouting,
	tryParseContactSource
} from "./routing";

export type OverviewPageProps = {
	source: AddressBookContactSource;
	department?: string;
	officeLocation?: string;
	onNavigate: NavigationCallback;
};

export const OverviewPage = (
	props: OverviewPageProps
): ReactElement => {
	const {
		source: pathSource,
		department,
		officeLocation,
		onNavigate
	} = props;

	const sourceSelectorValue: SourceSelectorValue = useContactSourceValue(
		pathSource,
		department,
		officeLocation
	);

	const navigateToItemHandler = useCallback(
		(contactId: string, source: AddressBookContactSource): void => {
			onNavigate(
				addressBookRouting.details({
					routeParams: {
						source: ensureNotNil(convertToSourcePath(source)),
						id: encodeIdForRouting(source, contactId)
					},
					queryParams: {
						department,
						officeLocation
					}
				})
			);
		},
		[
			onNavigate,
			department,
			officeLocation
		]
	);

	return (
		<Overview
			source={sourceSelectorValue.source}
			sourceFilter={sourceSelectorValue.filter}
			onNavigateToItem={navigateToItemHandler}
		/>
	);
};
OverviewPage.displayName = "AddressBookPage";

export type OverviewPageRouteParams = RouteParametersFromProps<
	OverviewPageProps,
	| "source"
>;

export type OverviewPageQueryParams = QueryParametersFromProps<
	OverviewPageProps,
	| "department"
	| "officeLocation"
>;

export const OverviewPageForRouter: FunctionComponent = connectToRouter(
	OverviewPage,
	{
		"source": {
			isRequired: true,
			tryParse: tryParseContactSource
		},
		"department": {
			isRequired: false,
			type: "query"
		},
		"officeLocation": {
			isRequired: false,
			type: "query"
		},
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);